import React from "react"
import TierRow from "./TierRow"
import {EditMode} from "../../../../resources/common"
import {CustomerInterestTier} from "../../../../resources/depositProduct"
import Button from "../../../../components/Button/Button"

interface TierListProps {
	tiers: CustomerInterestTier[]
	editMode: EditMode
	isDisabled?: boolean
	onChange: (tiers: CustomerInterestTier[]) => void
}

// Create default tiers when enabling tiered interest
export function createDefaultTiers(): CustomerInterestTier[] {
	return [
		{lowerBound: 0, higherBound: 1000000, formula: ""}, // Tier 1: $0 up to $10,000
		{lowerBound: 1000000, higherBound: undefined, formula: ""}, // Tier 2: $10,000 and above
	]
}

export default function TierList({tiers, editMode, isDisabled = false, onChange}: TierListProps) {
	const isViewMode = editMode === EditMode.VIEW
	const controlsDisabled = isViewMode || isDisabled

	const handleTierChange = (index: number, updatedTier: CustomerInterestTier) => {
		const newTiers = [...tiers]
		newTiers[index] = updatedTier

		// Auto-adjust next tier's lower bound to match this tier's upper bound exactly
		if (updatedTier.higherBound !== undefined && index < newTiers.length - 1) {
			newTiers[index + 1] = {
				...newTiers[index + 1],
				lowerBound: updatedTier.higherBound,
			}
		}

		onChange(newTiers)
	}

	const handleDeleteTier = (index: number) => {
		if (tiers.length <= 2) return // Minimum 2 tiers required
		const newTiers = tiers.filter((_, i) => i !== index)

		// Re-chain the tier boundaries to match exactly
		for (let i = 1; i < newTiers.length; i++) {
			if (newTiers[i - 1].higherBound !== undefined) {
				newTiers[i] = {
					...newTiers[i],
					lowerBound: newTiers[i - 1].higherBound,
				}
			}
		}

		onChange(newTiers)
	}

	const handleAddTier = () => {
		const lastTier = tiers[tiers.length - 1]
		const newLowerBound =
			lastTier.higherBound !== undefined
				? lastTier.higherBound
				: lastTier.lowerBound
				? lastTier.lowerBound + 1000000
				: undefined

		// Update the previous last tier to have an upper bound if it was "and above"
		const updatedTiers = [...tiers]
		if (lastTier.higherBound === undefined) {
			updatedTiers[updatedTiers.length - 1] = {
				...lastTier,
				higherBound: newLowerBound,
			}
		}

		// Add new tier with lowerBound matching previous tier's higherBound
		updatedTiers.push({
			lowerBound: newLowerBound,
			higherBound: undefined, // New tier is "and above"
			formula: "",
		})

		onChange(updatedTiers)
	}

	return (
		<div className="tier-list">
			{tiers.map((tier, index) => (
				<TierRow
					key={index}
					index={index}
					tier={tier}
					isFirst={index === 0}
					isSecond={index === 1}
					editMode={editMode}
					isDisabled={isDisabled}
					onChange={(updatedTier) => handleTierChange(index, updatedTier)}
					onDelete={() => handleDeleteTier(index)}
				/>
			))}
			<Button
				type="button"
				variant="outline"
				className="add-tier-button"
				onClick={handleAddTier}
				disabled={controlsDisabled}
			>
				+ Add Tier
			</Button>
		</div>
	)
}
